/**
 * Shopping cart store — list of plans the user wants to buy in one session.
 *
 * For Phase B we keep it simple: one Culqi charge per plan (each charge has
 * its own card token). The cart is mostly a UI affordance so the user can
 * pick multiple packs before going through the modal, and we replay the
 * checkout sequentially.
 *
 * Subscription plans are mutually exclusive — only the latest-added one stays
 * in the cart, since adding two would just extend the expiry anyway and we
 * don't want to bill twice by accident.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Plan } from '@/features/billing/types';

export interface CartItem {
  plan: Plan;
  quantity: number;
}

interface CartState {
  items: CartItem[];
  /** Add one unit of a plan (subscriptions: replace any other subscription). */
  add: (plan: Plan) => void;
  remove: (planId: number) => void;
  setQuantity: (planId: number, qty: number) => void;
  clear: () => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      add: (plan) =>
        set((state) => {
          // Subscription rule: only one subscription plan allowed in cart.
          const filtered = plan.is_subscription
            ? state.items.filter((it) => !it.plan.is_subscription)
            : state.items;
          const existing = filtered.find((it) => it.plan.id === plan.id);
          if (existing) {
            // Don't stack subscriptions; for token packs increment quantity.
            if (plan.is_subscription) return { items: filtered };
            return {
              items: filtered.map((it) =>
                it.plan.id === plan.id ? { ...it, quantity: it.quantity + 1 } : it,
              ),
            };
          }
          return { items: [...filtered, { plan, quantity: 1 }] };
        }),
      remove: (planId) =>
        set((state) => ({ items: state.items.filter((it) => it.plan.id !== planId) })),
      setQuantity: (planId, qty) =>
        set((state) => ({
          items: state.items
            .map((it) =>
              it.plan.id === planId ? { ...it, quantity: Math.max(1, qty) } : it,
            )
            .filter((it) => it.quantity > 0),
        })),
      clear: () => set({ items: [] }),
    }),
    { name: 'alphaconcreto-cart' },
  ),
);

export function cartTotalCents(items: CartItem[]): number {
  return items.reduce((acc, it) => acc + it.plan.price_cents * it.quantity, 0);
}
